import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';
import type { PaySchedule } from '@/types';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * All "day" boundaries (00:00 reset, daily views) are anchored to this zone.
 * Must match `functions/src/lib/dates.ts` (kept in sync manually, separate
 * Node projects).
 */
export const APP_TIMEZONE = 'Europe/Copenhagen';

/**
 * Earliest date the admin daily and monthly reporting views allow navigating
 * to — the app has no operational data before this date.
 */
export const ADMIN_REPORTING_MIN_DATE = '2026-08-01';

/** Earliest ending month selectable in monthly reports and personal history. */
export const ADMIN_REPORTING_MIN_MONTH = '2026-08';
export const DEFAULT_PAY_SCHEDULE: PaySchedule = 'pay-period';

/** Today's calendar date in the app timezone, formatted as `YYYY-MM-DD`. */
export const todayIsoDate = (): string => dayjs().tz(APP_TIMEZONE).format('YYYY-MM-DD');

/** The current calendar month in the app timezone, formatted as `YYYY-MM`. */
export const todayIsoMonth = (): string => dayjs().tz(APP_TIMEZONE).format('YYYY-MM');

/** The current pay period's ending month, formatted as `YYYY-MM`. */
export const todayIsoPeriodMonth = (): string => {
  const today = dayjs().tz(APP_TIMEZONE);
  return today.add(today.date() >= 21 ? 1 : 0, 'month').format('YYYY-MM');
};

/** Current report month for a user's assigned schedule. */
export const todayIsoReportMonth = (paySchedule: PaySchedule): string =>
  paySchedule === 'calendar-month' ? todayIsoMonth() : todayIsoPeriodMonth();

/** Returns a pay period named by its ending month, with an exclusive end date. */
export const getPayPeriod = (endingMonth: string) => {
  const endDateExclusive = dayjs.tz(`${endingMonth}-21`, APP_TIMEZONE);
  return {
    startDate: endDateExclusive.subtract(1, 'month').format('YYYY-MM-DD'),
    endDate: endDateExclusive.subtract(1, 'day').format('YYYY-MM-DD'),
    endDateExclusive: endDateExclusive.format('YYYY-MM-DD'),
  };
};

/** The report date range for the selected ending month and assigned schedule. */
export const getReportPeriod = (paySchedule: PaySchedule, endingMonth: string) => {
  if (paySchedule === 'calendar-month') {
    const startDate = dayjs.tz(`${endingMonth}-01`, APP_TIMEZONE);
    const endDateExclusive = startDate.add(1, 'month');
    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDateExclusive.subtract(1, 'day').format('YYYY-MM-DD'),
      endDateExclusive: endDateExclusive.format('YYYY-MM-DD'),
    };
  }
  return getPayPeriod(endingMonth);
};

/** Formats a report period range using the assigned schedule. */
export const formatReportPeriodRange = (paySchedule: PaySchedule, endingMonth: string): string => {
  const { startDate, endDate } = getReportPeriod(paySchedule, endingMonth);
  return `${formatIsoDate(startDate)} – ${formatIsoDate(endDate)}`;
};

/** Formats an ISO date string (`YYYY-MM-DD`) for display as `DD/MM/YYYY`. */
export const formatIsoDate = (isoDate: string): string =>
  dayjs.tz(isoDate, APP_TIMEZONE).format('DD/MM/YYYY');

/** Formats an ISO instant string for display in the app timezone. */
export const formatDateTime = (isoInstant: string): string =>
  dayjs(isoInstant).tz(APP_TIMEZONE).format('DD/MM/YYYY, HH:mm');

/** Formats an ISO instant string for display in the app timezone, including seconds. */
export const formatDateTimeWithSeconds = (isoInstant: string): string =>
  dayjs(isoInstant).tz(APP_TIMEZONE).format('DD/MM/YYYY, HH:mm:ss');
